import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { getLeadStages, createLeadStage, updateLeadStage, deleteLeadStage } from '../lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { UserCircle, Moon, Sun, LogOut, Shield, Wallet, Tags, Plus, Edit, Trash2, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

const COLORS = [
  { value: 'gray', label: 'Серый', class: 'badge-gray' },
  { value: 'blue', label: 'Синий', class: 'badge-blue' },
  { value: 'yellow', label: 'Жёлтый', class: 'badge-yellow' },
  { value: 'green', label: 'Зелёный', class: 'badge-green' },
  { value: 'red', label: 'Красный', class: 'badge-red' },
];

export default function SettingsPage() {
  const { user, logout, isAdmin } = useAuth();
  const { theme, toggleTheme, isDark } = useTheme();
  const navigate = useNavigate();

  // Lead Stages
  const [stages, setStages] = useState([]);
  const [loadingStages, setLoadingStages] = useState(false);
  const [stageDialogOpen, setStageDialogOpen] = useState(false);
  const [editingStage, setEditingStage] = useState(null);
  const [stageName, setStageName] = useState('');
  const [stageColor, setStageColor] = useState('gray');
  const [stageOrder, setStageOrder] = useState(0);
  const [savingStage, setSavingStage] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingStage, setDeletingStage] = useState(null);

  const fetchStages = useCallback(async () => {
    if (!isAdmin) return;
    try {
      setLoadingStages(true);
      const data = await getLeadStages();
      setStages(data);
    } catch (err) {
      console.error('Failed to fetch stages:', err);
    } finally {
      setLoadingStages(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchStages();
  }, [fetchStages]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const openCreateStageDialog = () => {
    setEditingStage(null);
    setStageName('');
    setStageColor('gray');
    setStageOrder(stages.length);
    setStageDialogOpen(true);
  };

  const openEditStageDialog = (stage) => {
    setEditingStage(stage);
    setStageName(stage.name);
    setStageColor(stage.color);
    setStageOrder(stage.order);
    setStageDialogOpen(true);
  };

  const handleSaveStage = async () => {
    if (!stageName) return;

    try {
      setSavingStage(true);
      const data = {
        name: stageName,
        color: stageColor,
        order: stageOrder,
      };

      if (editingStage) {
        await updateLeadStage(editingStage.id, data);
        toast.success('Этап обновлён');
      } else {
        await createLeadStage(data);
        toast.success('Этап создан');
      }

      setStageDialogOpen(false);
      fetchStages();
    } catch (err) {
      console.error('Failed to save stage:', err);
      toast.error(err.response?.data?.detail || 'Ошибка сохранения');
    } finally {
      setSavingStage(false);
    }
  };

  const confirmDeleteStage = (stage) => {
    setDeletingStage(stage);
    setDeleteDialogOpen(true);
  };

  const handleDeleteStage = async () => {
    if (!deletingStage) return;

    try {
      await deleteLeadStage(deletingStage.id);
      toast.success('Этап удалён');
      setDeleteDialogOpen(false);
      setDeletingStage(null);
      fetchStages();
    } catch (err) {
      console.error('Failed to delete stage:', err);
      toast.error(err.response?.data?.detail || 'Ошибка удаления');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Настройки</h1>
        <p className="text-muted-foreground">Управление аккаунтом и приложением</p>
      </div>

      {/* User Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <UserCircle className="h-5 w-5" />
            Профиль
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <UserCircle className="h-8 w-8 text-primary" />
            </div>
            <div>
              <p className="text-lg font-medium" data-testid="user-name">{user?.username}</p>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                {isAdmin && <Shield className="h-4 w-4" />}
                <span data-testid="user-role">
                  {user?.role === 'admin' ? 'Администратор' : 'Менеджер'}
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Theme Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            {isDark ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
            Оформление
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between py-1">
            <div className="space-y-1">
              <Label htmlFor="dark-mode" className="text-base">Тёмная тема</Label>
              <p className="text-sm text-muted-foreground">
                Переключение между светлой и тёмной темой
              </p>
            </div>
            <Switch
              id="dark-mode"
              checked={isDark}
              onCheckedChange={toggleTheme}
              data-testid="theme-toggle"
            />
          </div>
        </CardContent>
      </Card>

      {/* Lead Stages (Admin only) */}
      {isAdmin && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Tags className="h-5 w-5" />
                Этапы лидов
              </CardTitle>
              <Button size="sm" onClick={openCreateStageDialog} data-testid="create-stage-btn">
                <Plus className="h-4 w-4 mr-1" />
                Добавить
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loadingStages ? (
              <div className="flex justify-center py-4">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : stages.length === 0 ? (
              <p className="text-muted-foreground text-center py-4">Нет этапов</p>
            ) : (
              <div className="space-y-2">
                {stages.map((stage) => (
                  <div
                    key={stage.id}
                    className="flex items-center justify-between p-3 rounded-lg border"
                    data-testid={`stage-${stage.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-muted-foreground w-6">{stage.order + 1}.</span>
                      <span
                        className={cn(
                          'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                          `badge-${stage.color}`
                        )}
                      >
                        {stage.name}
                      </span>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => openEditStageDialog(stage)}
                        data-testid={`edit-stage-${stage.id}`}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => confirmDeleteStage(stage)}
                        data-testid={`delete-stage-${stage.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* App Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Wallet className="h-5 w-5" />
            О приложении
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Версия</span>
            <span>2.0.0</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Название</span>
            <span>Expense Tracker</span>
          </div>
        </CardContent>
      </Card>

      <Separator />

      {/* Logout */}
      <Button
        variant="destructive"
        className="w-full"
        onClick={handleLogout}
        data-testid="settings-logout-btn"
      >
        <LogOut className="h-4 w-4 mr-2" />
        Выйти из аккаунта
      </Button>

      {/* Stage Dialog */}
      <Dialog open={stageDialogOpen} onOpenChange={setStageDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingStage ? 'Редактировать этап' : 'Добавить этап'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Название</Label>
              <Input
                placeholder="Введите название"
                value={stageName}
                onChange={(e) => setStageName(e.target.value)}
                data-testid="stage-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Цвет</Label>
              <Select value={stageColor} onValueChange={setStageColor}>
                <SelectTrigger data-testid="stage-color-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COLORS.map((c) => (
                    <SelectItem key={c.value} value={c.value}>
                      <div className="flex items-center gap-2">
                        <span className={cn('inline-block w-3 h-3 rounded-full', c.class)} />
                        {c.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Порядок</Label>
              <Input
                type="number"
                min="0"
                value={stageOrder}
                onChange={(e) => setStageOrder(parseInt(e.target.value) || 0)}
                data-testid="stage-order-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setStageDialogOpen(false)}>
              Отмена
            </Button>
            <Button
              onClick={handleSaveStage}
              disabled={!stageName || savingStage}
              data-testid="confirm-save-stage"
            >
              {savingStage ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Сохранить'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Stage Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Удалить этап?</AlertDialogTitle>
            <AlertDialogDescription>
              Вы уверены, что хотите удалить этап "{deletingStage?.name}"?
              Если есть лиды с этим этапом, удаление будет невозможно.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteStage}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-stage"
            >
              Удалить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
